export interface NavItem {
  type: 'link' | 'toggle'
  id: string
  label: string
  to?: string
  requiresAuth?: boolean
  width: number
}

/** What the "More" overflow menu needs to render an item (no layout width). */
export type OverflowItem = Omit<NavItem, 'width'>

export const NAV_LINKS: NavItem[] = [
  { type: 'link', id: 'browse', label: 'Browse', to: '/browse/cards', requiresAuth: false, width: 70 },
  { type: 'link', id: 'decks', label: 'Decks', to: '/decks', requiresAuth: true, width: 70 },
  { type: 'link', id: 'binders', label: 'Binders', to: '/binders', requiresAuth: true, width: 80 },
  { type: 'link', id: 'list', label: 'Buy/Sell', to: '/list/buy', requiresAuth: true, width: 80 },
]

export const TOGGLE_ITEMS: NavItem[] = [
  { type: 'toggle', id: 'collapse-toggle', label: 'Player', requiresAuth: true, width: 100 },
  { type: 'toggle', id: 'hidden-toggle', label: 'Filtered', requiresAuth: true, width: 100 },
]

const MORE_BUTTON_WIDTH = 70

/** How many leading items fit in `availableWidth`, reserving room for "More" when anything is left over. */
function countFitting(
  items: NavItem[],
  startWidth: number,
  availableWidth: number,
  extraAfter: (i: number) => boolean,
) {
  let used = startWidth
  let count = 0
  for (let i = 0; i < items.length; i++) {
    const reservedForMore = extraAfter(i) ? MORE_BUTTON_WIDTH : 0
    if (used + items[i].width + reservedForMore > availableWidth) break
    used += items[i].width
    count++
  }
  return { count, used }
}

/** Priority fitting: links first, then toggles; whatever doesn't fit overflows into "More". */
export function fitNavItems(links: NavItem[], toggles: NavItem[], availableWidth: number) {
  const linkFit = countFitting(links, 0, availableWidth, (i) => links.length - i - 1 + toggles.length > 0)
  const toggleFit = countFitting(
    toggles,
    linkFit.used,
    availableWidth,
    (i) => toggles.length - i - 1 > 0 || linkFit.count < links.length,
  )

  return {
    visibleLinks: links.slice(0, linkFit.count),
    visibleToggles: toggles.slice(0, toggleFit.count),
    overflowItems: [...links.slice(linkFit.count), ...toggles.slice(toggleFit.count)],
  }
}
